/**
 * Defines a color palette for Material Design themes.
 *
 * This type represents a mapping of material color tokens to their corresponding color values.
 * It is used for different theme variations.
 */
export type MaterialColorPalette = Record<keyof typeof MaterialColorTokenKeys, string>;

/**
 * Defines the JSON Schema for Material Color Tokens.
 *
 * This interface represents the different color palettes used in the Material Design System.
 * Each palette corresponds to a specific theme variant:
 * - `light`: Standard light theme.
 * - `dark`: Standard dark theme.
 * - `light-high-contrast`: High-contrast version of the light theme.
 * - `dark-high-contrast`: High-contrast version of the dark theme.
 *
 * These palettes ensure consistent theming across Material Design components.
 */
export interface MaterialColorPalettes {
  light: MaterialColorPalette;
  dark: MaterialColorPalette;
  'light-high-contrast': MaterialColorPalette;
  'dark-high-contrast': MaterialColorPalette;
}

/**
 * Defines all Material Color Tokens.
 *
 * This object serves as the authoritative source for Material Color Tokens in JavaScript.
 * It ensures consistency across the Material Design System and is used to generate
 * the corresponding TypeScript interface through utility methods.
 *
 * Each key represents a Material Design color token, which follows a standardized naming
 * convention for theming and styling purposes.
 *
 * **Important:** This is the only place where Material Color Tokens should be listed in JavaScript.
 *
 * Only the keys are ever read (the values are placeholders). They are `null` rather than
 * `undefined` because type tooling that parses this literal (the JSON Schema generator in
 * tools/json-schema) can represent a null literal but not the `undefined` identifier.
 */
export const MaterialColorTokenKeys = {
  '--mat-sys-primary': null,
  '--mat-sys-on-primary': null,
  '--mat-sys-primary-container': null,
  '--mat-sys-on-primary-container': null,
  '--mat-sys-inverse-primary': null,
  '--mat-sys-primary-fixed': null,
  '--mat-sys-primary-fixed-dim': null,
  '--mat-sys-on-primary-fixed': null,
  '--mat-sys-on-primary-fixed-variant': null,
  '--mat-sys-secondary': null,
  '--mat-sys-on-secondary': null,
  '--mat-sys-secondary-container': null,
  '--mat-sys-on-secondary-container': null,
  '--mat-sys-secondary-fixed': null,
  '--mat-sys-secondary-fixed-dim': null,
  '--mat-sys-on-secondary-fixed': null,
  '--mat-sys-on-secondary-fixed-variant': null,
  '--mat-sys-tertiary': null,
  '--mat-sys-on-tertiary': null,
  '--mat-sys-tertiary-container': null,
  '--mat-sys-on-tertiary-container': null,
  '--mat-sys-tertiary-fixed': null,
  '--mat-sys-tertiary-fixed-dim': null,
  '--mat-sys-on-tertiary-fixed': null,
  '--mat-sys-on-tertiary-fixed-variant': null,
  '--mat-sys-background': null,
  '--mat-sys-on-background': null,
  '--mat-sys-surface': null,
  '--mat-sys-surface-dim': null,
  '--mat-sys-surface-bright': null,
  '--mat-sys-surface-container-low': null,
  '--mat-sys-surface-container-lowest': null,
  '--mat-sys-surface-container': null,
  '--mat-sys-surface-container-high': null,
  '--mat-sys-surface-container-highest': null,
  '--mat-sys-on-surface': null,
  '--mat-sys-shadow': null,
  '--mat-sys-scrim': null,
  '--mat-sys-surface-tint': null,
  '--mat-sys-inverse-surface': null,
  '--mat-sys-inverse-on-surface': null,
  '--mat-sys-outline': null,
  '--mat-sys-outline-variant': null,
  '--mat-sys-neutral10': null,
  '--mat-sys-error': null,
  '--mat-sys-on-error': null,
  '--mat-sys-error-container': null,
  '--mat-sys-on-error-container': null,
  '--mat-sys-surface-variant': null,
  '--mat-sys-on-surface-variant': null,
  '--mat-sys-neutral-variant20': null,
};

/**
 * Default Material Color Tokens used in Drimify.
 *
 * This object defines the standard color palettes for Drimify's Material Design system.
 * It includes predefined themes that ensure consistent color representation across
 * various UI components.
 *
 * Available themes:
 * - `light`: Standard light theme.
 * - `dark`: Standard dark theme.
 * - `light-high-contrast`: High-contrast version of the light theme.
 * - `dark-high-contrast`: High-contrast version of the dark theme.
 *
 * These tokens serve as the foundation for theming in Drimify and are structured
 * to align with Material Design guidelines.
 */
export const DefaultMaterialColorTokens: MaterialColorPalettes = {
  light: {
    '--mat-sys-primary': '#585f68',
    '--mat-sys-on-primary': '#ffffff',
    '--mat-sys-primary-container': '#dce3ee',
    '--mat-sys-on-primary-container': '#151c24',
    '--mat-sys-inverse-primary': '#c0c7d2',
    '--mat-sys-primary-fixed': '#dce3ee',
    '--mat-sys-primary-fixed-dim': '#c0c7d2',
    '--mat-sys-on-primary-fixed': '#151c24',
    '--mat-sys-on-primary-fixed-variant': '#404750',
    '--mat-sys-secondary': '#5c5e62',
    '--mat-sys-on-secondary': '#ffffff',
    '--mat-sys-secondary-container': '#e1e2e7',
    '--mat-sys-on-secondary-container': '#191c1f',
    '--mat-sys-secondary-fixed': '#e1e2e7',
    '--mat-sys-secondary-fixed-dim': '#c5c6cb',
    '--mat-sys-on-secondary-fixed': '#191c1f',
    '--mat-sys-on-secondary-fixed-variant': '#44474b',
    '--mat-sys-tertiary': '#665b65',
    '--mat-sys-on-tertiary': '#ffffff',
    '--mat-sys-tertiary-container': '#eedeea',
    '--mat-sys-on-tertiary-container': '#211921',
    '--mat-sys-tertiary-fixed': '#eedeea',
    '--mat-sys-tertiary-fixed-dim': '#d1c2ce',
    '--mat-sys-on-tertiary-fixed': '#211921',
    '--mat-sys-on-tertiary-fixed-variant': '#4e444d',
    '--mat-sys-background': '#fcf8f9',
    '--mat-sys-on-background': '#1b1b1c',
    '--mat-sys-surface': '#fcf8f9',
    '--mat-sys-surface-dim': '#dcd9da',
    '--mat-sys-surface-bright': '#fcf8f9',
    '--mat-sys-surface-container-low': '#f6f3f3',
    '--mat-sys-surface-container-lowest': '#ffffff',
    '--mat-sys-surface-container': '#f0eded',
    '--mat-sys-surface-container-high': '#eae7e8',
    '--mat-sys-surface-container-highest': '#e5e2e2',
    '--mat-sys-on-surface': '#1b1b1c',
    '--mat-sys-shadow': '#000000',
    '--mat-sys-scrim': '#000000',
    '--mat-sys-surface-tint': '#585f68',
    '--mat-sys-inverse-surface': '#303031',
    '--mat-sys-inverse-on-surface': '#f3f0f0',
    '--mat-sys-outline': '#75777b',
    '--mat-sys-outline-variant': '#c5c6cb',
    '--mat-sys-neutral10': '#1b1b1c',
    '--mat-sys-error': '#ba1a1a',
    '--mat-sys-on-error': '#ffffff',
    '--mat-sys-error-container': '#ffdad6',
    '--mat-sys-on-error-container': '#410002',
    '--mat-sys-surface-variant': '#e1e2e7',
    '--mat-sys-on-surface-variant': '#44474b',
    '--mat-sys-neutral-variant20': '#2e3134',
  },
  dark: {
    '--mat-sys-primary': '#c0c7d2',
    '--mat-sys-on-primary': '#2a3139',
    '--mat-sys-primary-container': '#404750',
    '--mat-sys-on-primary-container': '#dce3ee',
    '--mat-sys-inverse-primary': '#585f68',
    '--mat-sys-primary-fixed': '#dce3ee',
    '--mat-sys-primary-fixed-dim': '#c0c7d2',
    '--mat-sys-on-primary-fixed': '#151c24',
    '--mat-sys-on-primary-fixed-variant': '#404750',
    '--mat-sys-secondary': '#c5c6cb',
    '--mat-sys-on-secondary': '#2e3134',
    '--mat-sys-secondary-container': '#44474b',
    '--mat-sys-on-secondary-container': '#e1e2e7',
    '--mat-sys-secondary-fixed': '#e1e2e7',
    '--mat-sys-secondary-fixed-dim': '#c5c6cb',
    '--mat-sys-on-secondary-fixed': '#191c1f',
    '--mat-sys-on-secondary-fixed-variant': '#44474b',
    '--mat-sys-tertiary': '#d1c2ce',
    '--mat-sys-on-tertiary': '#372d37',
    '--mat-sys-tertiary-container': '#4e444d',
    '--mat-sys-on-tertiary-container': '#eedeea',
    '--mat-sys-tertiary-fixed': '#eedeea',
    '--mat-sys-tertiary-fixed-dim': '#d1c2ce',
    '--mat-sys-on-tertiary-fixed': '#211921',
    '--mat-sys-on-tertiary-fixed-variant': '#4e444d',
    '--mat-sys-background': '#131314',
    '--mat-sys-on-background': '#e5e2e2',
    '--mat-sys-surface': '#131314',
    '--mat-sys-surface-dim': '#131314',
    '--mat-sys-surface-bright': '#39393a',
    '--mat-sys-surface-container-low': '#1b1b1c',
    '--mat-sys-surface-container-lowest': '#0e0e0f',
    '--mat-sys-surface-container': '#201f20',
    '--mat-sys-surface-container-high': '#2a2a2a',
    '--mat-sys-surface-container-highest': '#353535',
    '--mat-sys-on-surface': '#e5e2e2',
    '--mat-sys-shadow': '#000000',
    '--mat-sys-scrim': '#000000',
    '--mat-sys-surface-tint': '#c0c7d2',
    '--mat-sys-inverse-surface': '#e5e2e2',
    '--mat-sys-inverse-on-surface': '#303031',
    '--mat-sys-outline': '#8f9195',
    '--mat-sys-outline-variant': '#44474b',
    '--mat-sys-neutral10': '#1b1b1c',
    '--mat-sys-error': '#ffb4ab',
    '--mat-sys-on-error': '#690005',
    '--mat-sys-error-container': '#93000a',
    '--mat-sys-on-error-container': '#ffdad6',
    '--mat-sys-surface-variant': '#44474b',
    '--mat-sys-on-surface-variant': '#c5c6cb',
    '--mat-sys-neutral-variant20': '#2e3134',
  },
  'light-high-contrast': {
    '--mat-sys-primary': '#262d35',
    '--mat-sys-on-primary': '#ffffff',
    '--mat-sys-primary-container': '#434a53',
    '--mat-sys-on-primary-container': '#ffffff',
    '--mat-sys-inverse-primary': '#c0c7d2',
    '--mat-sys-primary-fixed': '#434a53',
    '--mat-sys-primary-fixed-dim': '#2c333c',
    '--mat-sys-on-primary-fixed': '#ffffff',
    '--mat-sys-on-primary-fixed-variant': '#ffffff',
    '--mat-sys-secondary': '#2a2c30',
    '--mat-sys-on-secondary': '#ffffff',
    '--mat-sys-secondary-container': '#47494d',
    '--mat-sys-on-secondary-container': '#ffffff',
    '--mat-sys-secondary-fixed': '#47494d',
    '--mat-sys-secondary-fixed-dim': '#303336',
    '--mat-sys-on-secondary-fixed': '#ffffff',
    '--mat-sys-on-secondary-fixed-variant': '#ffffff',
    '--mat-sys-tertiary': '#322932',
    '--mat-sys-on-tertiary': '#ffffff',
    '--mat-sys-tertiary-container': '#504650',
    '--mat-sys-on-tertiary-container': '#ffffff',
    '--mat-sys-tertiary-fixed': '#504650',
    '--mat-sys-tertiary-fixed-dim': '#393039',
    '--mat-sys-on-tertiary-fixed': '#ffffff',
    '--mat-sys-on-tertiary-fixed-variant': '#ffffff',
    '--mat-sys-background': '#fcf8f9',
    '--mat-sys-on-background': '#1b1b1c',
    '--mat-sys-surface': '#fcf8f9',
    '--mat-sys-surface-dim': '#bab8b9',
    '--mat-sys-surface-bright': '#fcf8f9',
    '--mat-sys-surface-container-low': '#f3f0f0',
    '--mat-sys-surface-container-lowest': '#ffffff',
    '--mat-sys-surface-container': '#e5e2e2',
    '--mat-sys-surface-container-high': '#d6d4d4',
    '--mat-sys-surface-container-highest': '#c8c6c6',
    '--mat-sys-on-surface': '#000000',
    '--mat-sys-shadow': '#000000',
    '--mat-sys-scrim': '#000000',
    '--mat-sys-surface-tint': '#585f68',
    '--mat-sys-inverse-surface': '#303031',
    '--mat-sys-inverse-on-surface': '#ffffff',
    '--mat-sys-outline': '#2a2c30',
    '--mat-sys-outline-variant': '#47494d',
    '--mat-sys-neutral10': '#1b1b1c',
    '--mat-sys-error': '#600004',
    '--mat-sys-on-error': '#ffffff',
    '--mat-sys-error-container': '#98000a',
    '--mat-sys-on-error-container': '#ffffff',
    '--mat-sys-surface-variant': '#e1e2e7',
    '--mat-sys-on-surface-variant': '#000000',
    '--mat-sys-neutral-variant20': '#2e3134',
  },
  'dark-high-contrast': {
    '--mat-sys-primary': '#eaf0fc',
    '--mat-sys-on-primary': '#000000',
    '--mat-sys-primary-container': '#bcc3ce',
    '--mat-sys-on-primary-container': '#050c13',
    '--mat-sys-inverse-primary': '#424951',
    '--mat-sys-primary-fixed': '#dce3ee',
    '--mat-sys-primary-fixed-dim': '#c0c7d2',
    '--mat-sys-on-primary-fixed': '#000000',
    '--mat-sys-on-primary-fixed-variant': '#0b1219',
    '--mat-sys-secondary': '#eff0f4',
    '--mat-sys-on-secondary': '#000000',
    '--mat-sys-secondary-container': '#c1c2c7',
    '--mat-sys-on-secondary-container': '#090b0f',
    '--mat-sys-secondary-fixed': '#e1e2e7',
    '--mat-sys-secondary-fixed-dim': '#c5c6cb',
    '--mat-sys-on-secondary-fixed': '#000000',
    '--mat-sys-on-secondary-fixed-variant': '#0f1115',
    '--mat-sys-tertiary': '#fcebf8',
    '--mat-sys-on-tertiary': '#000000',
    '--mat-sys-tertiary-container': '#cdbeca',
    '--mat-sys-on-tertiary-container': '#100911',
    '--mat-sys-tertiary-fixed': '#eedeea',
    '--mat-sys-tertiary-fixed-dim': '#d1c2ce',
    '--mat-sys-on-tertiary-fixed': '#000000',
    '--mat-sys-on-tertiary-fixed-variant': '#160f17',
    '--mat-sys-background': '#131314',
    '--mat-sys-on-background': '#e5e2e2',
    '--mat-sys-surface': '#131314',
    '--mat-sys-surface-dim': '#131314',
    '--mat-sys-surface-bright': '#505050',
    '--mat-sys-surface-container-low': '#201f20',
    '--mat-sys-surface-container-lowest': '#000000',
    '--mat-sys-surface-container': '#303031',
    '--mat-sys-surface-container-high': '#3c3b3c',
    '--mat-sys-surface-container-highest': '#474647',
    '--mat-sys-on-surface': '#ffffff',
    '--mat-sys-shadow': '#000000',
    '--mat-sys-scrim': '#000000',
    '--mat-sys-surface-tint': '#c0c7d2',
    '--mat-sys-inverse-surface': '#e5e2e2',
    '--mat-sys-inverse-on-surface': '#000000',
    '--mat-sys-outline': '#eff0f5',
    '--mat-sys-outline-variant': '#c1c2c7',
    '--mat-sys-neutral10': '#1b1b1c',
    '--mat-sys-error': '#ffece9',
    '--mat-sys-on-error': '#000000',
    '--mat-sys-error-container': '#ffaea4',
    '--mat-sys-on-error-container': '#220001',
    '--mat-sys-surface-variant': '#44474b',
    '--mat-sys-on-surface-variant': '#ffffff',
    '--mat-sys-neutral-variant20': '#2e3134',
  },
};

/**
 * Generates system tokens from the provided Material theme.
 *
 * This function takes an optional `MaterialColorTokens` object and returns a set
 * of system tokens based on the provided theme. If no theme is provided, it returns `undefined`.
 *
 * @param theme - The theme containing Material Color Tokens.
 *
 * @returns The generated system tokens or `undefined` if no theme is provided.
 */
export const GenerateMaterialColorSystemTokens = (theme?: MaterialColorTokens) => {
  if (!theme) {
    return undefined;
  }

  return {
    ...theme,
  };
};

/**
 * Interface representing the generated Material Color Tokens.
 *
 * This interface extends `MaterialColorPalettes`, making all properties optional.
 * It allows flexibility in defining color tokens while maintaining compatibility
 * with Material Design theming.
 */
export interface MaterialColorTokens extends Partial<MaterialColorPalettes> {}
